import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CanceledError } from "axios";
import axios from "../services/axios";
import { DongleIpDetectStatus } from "../types/dongleIpDetect";

const MAX_DETECT_DURATION_MS = 60 * 60 * 1000;
const FAST_POLL_PHASE_MS = 5 * 60 * 1000;
const POLL_INTERVAL_FAST_MS = 5000;
const POLL_INTERVAL_SLOW_MS = 30000;

interface UseDongleIpDetectOptions {
  deviceId: string | undefined;
  enabled?: boolean;
}

export const useDongleIpDetect = ({
  deviceId,
  enabled = true,
}: UseDongleIpDetectOptions) => {
  const { t } = useTranslation("settings");
  const [status, setStatus] = useState<DongleIpDetectStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const httpAbortRef = useRef<AbortController | null>(null);
  /** Keeps latest fetchStatus without putting fetchStatus in effect deps (avoids reset/cancel loops). */
  const fetchStatusRef = useRef<() => Promise<boolean>>(async () => false);

  const abortInflightHttp = useCallback(() => {
    httpAbortRef.current?.abort();
    httpAbortRef.current = null;
  }, []);

  const prepareRequestSignal = useCallback(() => {
    httpAbortRef.current?.abort();
    const controller = new AbortController();
    httpAbortRef.current = controller;
    return controller.signal;
  }, []);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    abortInflightHttp();
    clearPollTimer();
  }, [abortInflightHttp, clearPollTimer]);

  const stopDetectRequest = useCallback(
    async (silent: boolean = false, signal?: AbortSignal) => {
      if (!deviceId) return false;
      try {
        const response = await axios.post(`/api-v1/device/${deviceId}/stop-dongle-detect/`, undefined, {
          signal,
        });
        return response.data.status === "dongle_detect_stopped";
      } catch (err: unknown) {
        if (err instanceof CanceledError) {
          return false;
        }
        if (silent) {
          console.error("Failed to stop dongle_ip_detect in shadow:", err);
          return false;
        }
        throw err;
      }
    },
    [deviceId],
  );

  const fetchStatus = useCallback(async (): Promise<boolean> => {
    if (!deviceId) return false;
    if (!isMountedRef.current) return false;

    const signal = prepareRequestSignal();

    try {
      const response = await axios.get(`/api-v1/device/${deviceId}/dongle-detect-status/`, {
        signal,
      });
      if (!isMountedRef.current) return false;

      const data: DongleIpDetectStatus = response.data;
      setStatus(data);

      if (data.has_results) {
        stopPolling();
        setLoading(false);
        startTimeRef.current = null;
        return false;
      }

      if (startTimeRef.current && Date.now() - startTimeRef.current > MAX_DETECT_DURATION_MS) {
        stopPolling();
        setError(
          t("home_settings_dongle_detect_timeout", "Dongle detection timed out. Please try again."),
        );
        setLoading(false);
        startTimeRef.current = null;
        const stopSignal = prepareRequestSignal();
        await stopDetectRequest(true, stopSignal);
        if (!isMountedRef.current) return false;
        try {
          const refreshSignal = prepareRequestSignal();
          const refresh = await axios.get(`/api-v1/device/${deviceId}/dongle-detect-status/`, {
            signal: refreshSignal,
          });
          if (!isMountedRef.current) return false;
          setStatus(refresh.data as DongleIpDetectStatus);
        } catch (refreshErr: unknown) {
          if (refreshErr instanceof CanceledError) {
            return false;
          }
        }
        return false;
      }

      return true;
    } catch (err: unknown) {
      if (err instanceof CanceledError) {
        return false;
      }
      if (!isMountedRef.current) return false;
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      setError(ax.response?.data?.error || ax.message || "Failed to fetch dongle detect status");
      setLoading(false);
      stopPolling();
      return false;
    }
  }, [deviceId, prepareRequestSignal, stopPolling, stopDetectRequest, t]);

  fetchStatusRef.current = fetchStatus;

  const startDetect = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);
    startTimeRef.current = Date.now();

    const startSignal = prepareRequestSignal();

    try {
      const response = await axios.post(
        `/api-v1/device/${deviceId}/start-dongle-detect/`,
        undefined,
        { signal: startSignal },
      );
      if (!isMountedRef.current) return;
      if (response.data.status === "dongle_detect_started") {
        await fetchStatusRef.current();
        if (!isMountedRef.current) return;
        setLoading(false);
      }
    } catch (err: unknown) {
      if (err instanceof CanceledError) {
        return;
      }
      if (!isMountedRef.current) return;
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      setError(ax.response?.data?.error || ax.message || "Failed to start dongle IP detection");
      setLoading(false);
      startTimeRef.current = null;
    }
  }, [deviceId, prepareRequestSignal]);

  const stopDetect = useCallback(async () => {
    if (!deviceId) return;

    setLoading(true);
    setError(null);
    stopPolling();

    const stopSignal = prepareRequestSignal();

    try {
      const stopped = await stopDetectRequest(false, stopSignal);
      if (!isMountedRef.current) return;
      if (stopped) {
        await fetchStatusRef.current();
        if (!isMountedRef.current) return;
        setLoading(false);
        startTimeRef.current = null;
      } else {
        setError("Failed to stop dongle IP detection");
        setLoading(false);
      }
    } catch (err: unknown) {
      if (err instanceof CanceledError) {
        return;
      }
      if (!isMountedRef.current) return;
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      setError(ax.response?.data?.error || ax.message || "Failed to stop dongle IP detection");
      setLoading(false);
    }
  }, [deviceId, stopDetectRequest, stopPolling, prepareRequestSignal]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (enabled && deviceId) {
      void fetchStatusRef.current();
    }
  }, [enabled, deviceId]);

  useEffect(() => {
    clearPollTimer();

    if (!enabled || !status?.dongle_ip_detect || status?.has_results) {
      return;
    }

    const scheduleNext = () => {
      const start = startTimeRef.current;
      const elapsed = start != null ? Date.now() - start : 0;
      const delay =
        elapsed < FAST_POLL_PHASE_MS ? POLL_INTERVAL_FAST_MS : POLL_INTERVAL_SLOW_MS;

      pollTimerRef.current = setTimeout(async () => {
        pollTimerRef.current = null;
        const shouldContinue = await fetchStatusRef.current();
        if (!shouldContinue) return;
        if (!startTimeRef.current) return;
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      stopPolling();
    };
  }, [enabled, status?.dongle_ip_detect, status?.has_results, stopPolling, clearPollTimer]);

  return {
    status,
    loading,
    error,
    startDetect,
    stopDetect,
    fetchStatus,
    clearError: () => setError(null),
  };
};
